// ============================================================================
// ANALYTICS API — Client-side rollup read helpers (Stage 7)
//
// Invariant: ZERO collection scans. ZERO PHI reads.
// Every read is a single getDoc() on an exact rollup path.
//
// Rollup doc path: tenants/{tenantId}/rollups/daily/dates/{YYYY-MM-DD}
// Written by: Cloud Functions (onMetricEventCreate_updateDailyRollup
//             + onWorkflowEventCreate_updateDailyRollup)
// Read by: OpsHomeView, PerformanceView, DeepDiveView
//
// Rollup doc fields (merged from both CF triggers):
//   counts.*           — gate metric event counts (existing)
//   value.*            — minutes reclaimed (existing)
//   breakdowns.*       — by specialty / by pack (existing)
//   flow.created       — WORKFLOW_CREATED events (Stage 7)
//   flow.needsInfo     — STATUS_CHANGED → NEEDS_INFO (Stage 7)
//   flow.readyToSchedule — STATUS_CHANGED → READY_TO_SCHEDULE (Stage 7)
//   flow.scheduled     — SCHEDULED_SET events (Stage 7)
//   flow.seen          — SEEN_SET events (Stage 7)
//   flow.closedLoop    — CLOSED_LOOP_SET events (Stage 7)
//   rework.requestsCreated — REWORK_REQUEST_CREATED events (Stage 7)
//   rework.touchesLogged   — REWORK_TOUCH_LOGGED events (Stage 7)
//   rework.resolved        — REWORK_RESOLVED events (Stage 7)
// ============================================================================

import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import PATHS from './firestorePaths.js';

function getDb() {
  return getFirestore(getApp());
}

// ============================================================================
// DATE HELPERS
// ============================================================================

/**
 * Returns the YYYY-MM-DD string for today in UTC.
 * @returns {string}
 */
export function getTodayDateId() {
  return getDateId(0);
}

/**
 * Returns the YYYY-MM-DD string for N days ago in UTC.
 * @param {number} daysAgo  0 = today, 1 = yesterday, etc.
 * @returns {string}
 */
export function getDateId(daysAgo = 0) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Builds an array of YYYY-MM-DD strings covering the last `nDays` days,
 * sorted oldest → today.
 *
 * Example: buildDateRange(3) on 2026-03-05 →
 *   ['2026-03-03', '2026-03-04', '2026-03-05']
 *
 * @param {number} nDays  Number of days (default: 7)
 * @returns {string[]}
 */
export function buildDateRange(nDays = 7) {
  return Array.from({ length: nDays }, (_, i) => getDateId(nDays - 1 - i));
}

// ============================================================================
// ROLLUP READS
// ============================================================================

/**
 * Fetch a single daily rollup document.
 *
 * @param {string} tenantId
 * @param {string} dateId   YYYY-MM-DD
 * @returns {Promise<object|null>}  Doc data or null if not found.
 */
export async function getDailyRollup(tenantId, dateId) {
  const db   = getDb();
  const snap = await getDoc(doc(db, PATHS.rollupDaily(tenantId, dateId)));
  return snap.exists() ? snap.data() : null;
}

/**
 * Fetch the ops snapshot document (speed + stuck).
 * Path: tenants/{tenantId}/rollups/ops/activeWorkflows/__snapshot
 * Written by recomputeAgingBucketsDaily (daily at 06:00 UTC).
 * Returns null if the doc does not exist yet (fresh tenant — treat as zeros).
 *
 * @param {string} tenantId
 * @returns {Promise<object|null>}
 */
export async function getOpsSnapshot(tenantId) {
  const db   = getDb();
  const snap = await getDoc(doc(db, PATHS.rollupOpsSnapshot(tenantId)));
  return snap.exists() ? snap.data() : null;
}

/**
 * Fetch multiple daily rollup documents in parallel.
 * Uses Promise.all over exact doc refs — NOT a collection query.
 *
 * Missing dates are returned as `{ dateId }` with no other fields.
 * Consumers should treat missing fields as 0.
 *
 * @param {string}   tenantId
 * @param {string[]} dateIds  Array of YYYY-MM-DD strings
 * @returns {Promise<Array<{dateId: string, [field]: any}>>}
 */
export async function getDailyRollups(tenantId, dateIds) {
  const db = getDb();
  const results = await Promise.all(
    dateIds.map(async (dateId) => {
      const snap = await getDoc(doc(db, PATHS.rollupDaily(tenantId, dateId)));
      return { dateId, ...(snap.exists() ? snap.data() : {}) };
    })
  );
  return results;
}

/**
 * Fetch entered-ops COHORT rollup docs for a set of cohort dates and return the
 * summed { enteredOps, closedLoop }.
 *
 * Path: tenants/{tenantId}/rollups/cohorts/enteredOps/{dateId}
 * Written by the workflowEvents CF (B3) + reconciled by the B4 backfill.
 * dateId = the referral's ENTERED-OPS cohort date (not the close date), so
 * closedLoop is attributed to the entry cohort — enabling a true cohort rate.
 *
 * Missing docs count as 0. Zero PHI, single getDoc per date (no scans).
 *
 * @param {string}   tenantId
 * @param {string[]} dateIds  YYYY-MM-DD cohort dates (UTC)
 * @returns {Promise<{ enteredOps: number, closedLoop: number, docsFound: number, lastComputedAtMs: number|null }>}
 */
export async function getCohortRollups(tenantId, dateIds) {
  const db = getDb();
  const snaps = await Promise.all(
    dateIds.map((dateId) =>
      getDoc(doc(db, 'tenants', tenantId, 'rollups', 'cohorts', 'enteredOps', dateId)),
    ),
  );
  let enteredOps = 0;
  let closedLoop = 0;
  let docsFound = 0;
  let lastComputedAtMs = null; // most recent cohort write across the window
  for (const snap of snaps) {
    if (!snap.exists()) continue;
    docsFound++;
    const d = snap.data();
    enteredOps += typeof d.enteredOps === 'number' ? d.enteredOps : 0;
    closedLoop += typeof d.closedLoop === 'number' ? d.closedLoop : 0;
    const lc = d.lastComputedAt;
    const ms = lc?.toMillis ? lc.toMillis() : (typeof lc?.seconds === 'number' ? lc.seconds * 1000 : null);
    if (ms != null && (lastComputedAtMs == null || ms > lastComputedAtMs)) lastComputedAtMs = ms;
  }
  return { enteredOps, closedLoop, docsFound, lastComputedAtMs };
}

/**
 * Fetch NOT-YET RECOVERY cohort rollup docs for a set of cohort dates and return
 * the summed counts.
 *
 * Path: tenants/{tenantId}/rollups/cohorts/notYet/{dateId}
 * Written by the metric-event CF (C3) + reconciled by the C4 backfill.
 * dateId = the date the referral was first marked NOT_YET (entry cohort date);
 * convertedByEngine/resolvedByOverride are attributed to that entry date, so the
 * rate is a true cohort: "of referrals first marked Not-Yet in this window, how
 * many recovered?".
 *
 * Missing docs count as 0. Zero PHI, single getDoc per date (no scans).
 *
 * @param {string}   tenantId
 * @param {string[]} dateIds  YYYY-MM-DD cohort dates (UTC)
 * @returns {Promise<{ notYet:number, convertedByEngine:number, resolvedByOverride:number, stillOpen:number, docsFound:number, lastComputedAtMs:number|null }>}
 */
export async function getNotYetCohortRollups(tenantId, dateIds) {
  const db = getDb();
  const snaps = await Promise.all(
    dateIds.map((dateId) =>
      getDoc(doc(db, 'tenants', tenantId, 'rollups', 'cohorts', 'notYet', dateId)),
    ),
  );
  let notYet = 0;
  let convertedByEngine = 0;
  let resolvedByOverride = 0;
  let stillOpen = 0;
  let docsFound = 0;
  let lastComputedAtMs = null;
  for (const snap of snaps) {
    if (!snap.exists()) continue;
    docsFound++;
    const d = snap.data();
    notYet             += typeof d.notYet === 'number' ? d.notYet : 0;
    convertedByEngine  += typeof d.convertedByEngine === 'number' ? d.convertedByEngine : 0;
    resolvedByOverride += typeof d.resolvedByOverride === 'number' ? d.resolvedByOverride : 0;
    stillOpen          += typeof d.stillOpen === 'number' ? d.stillOpen : 0;
    const lc = d.lastComputedAt;
    const ms = lc?.toMillis ? lc.toMillis() : (typeof lc?.seconds === 'number' ? lc.seconds * 1000 : null);
    if (ms != null && (lastComputedAtMs == null || ms > lastComputedAtMs)) lastComputedAtMs = ms;
  }
  return { notYet, convertedByEngine, resolvedByOverride, stillOpen, docsFound, lastComputedAtMs };
}

// ============================================================================
// ROLLUP FIELD ACCESSORS (safe — return 0 for missing fields)
// ============================================================================

/**
 * Safely read a nested field from a rollup doc. Returns 0 if absent.
 *
 * Handles two storage formats produced by the Cloud Functions:
 *
 *  1. Nested map   — e.g. aging: { needsInfo_0_3d: 0 }
 *                    Written via set(obj) where obj is a plain JS object.
 *                    Accessed as safeNum(doc, 'aging', 'needsInfo_0_3d').
 *
 *  2. Literal dot key — e.g. "flow.created": 4  or  "activity.createdCount": 4
 *                       Written via set/update with a dot-string key like
 *                       { 'flow.created': FieldValue.increment(1) }.
 *                       The Admin SDK set+merge stores this as a LITERAL field
 *                       name — the dot is NOT expanded into a nested map.
 *                       Firebase Console displays it as flow.created: 4 (flat).
 *
 * Strategy: try nested traversal first; if that yields nothing, join the path
 * with dots and try it as a literal field name on the root doc object.
 */
export function safeNum(rollup, ...path) {
  if (!rollup) return 0;

  // ── Primary: nested object traversal ──────────────────────────────────────
  let cur = rollup;
  for (const key of path) {
    if (cur == null || typeof cur !== 'object') { cur = undefined; break; }
    cur = cur[key];
  }
  if (typeof cur === 'number') return cur;

  // ── Fallback: literal dot-notation field name on the root doc ──────────────
  // e.g. safeNum(doc, 'flow', 'created') → doc['flow.created']
  const dotKey = path.join('.');
  const dotVal = rollup[dotKey];
  return typeof dotVal === 'number' ? dotVal : 0;
}

/**
 * Sum a specific field across an array of rollup docs.
 * @param {object[]} rollups  Array of rollup docs (may include empty objects)
 * @param {...string} path    Nested field path, e.g. ('flow', 'created')
 * @returns {number}
 */
export function sumField(rollups, ...path) {
  return rollups.reduce((acc, r) => acc + safeNum(r, ...path), 0);
}

/**
 * Sum a field across rollup docs, returning null if the field is absent from
 * ALL docs. This distinguishes "field never written" (null) from "field
 * written but value is 0" (0). Use for fields that may not exist in older
 * rollups (e.g. flowQuality.recoveredFromNotYet added after initial deploy).
 *
 * @param {object[]} rollups
 * @param {...string} path
 * @returns {number|null}  null if no rollup doc contains the field
 */
export function sumFieldOrNull(rollups, ...path) {
  let found = false;
  let total = 0;
  for (const r of rollups) {
    if (!r) continue;
    let cur = r;
    for (const key of path) {
      if (cur == null || typeof cur !== 'object') { cur = undefined; break; }
      cur = cur[key];
    }
    if (typeof cur !== 'number') {
      const dotKey = path.join('.');
      cur = r[dotKey];
    }
    if (typeof cur === 'number') {
      found = true;
      total += cur;
    }
  }
  return found ? total : null;
}

// ============================================================================
// FLOW QUALITY TOTALS — aggregate across a rollup array for the funnel page
// ============================================================================

/**
 * Compute Flow Quality funnel totals from an array of daily rollup docs.
 *
 * Primary fields: flowQuality.* (populated once CFs are deployed with the new
 * ENTERED_OPS + TRIAGE_EXCEPTION_RECORDED event listeners).
 *
 * Legacy fallback fields (flowQuality.* absent → 0): uses flow.* and counts.*
 * fields that were populated before the Flow Quality CF rollout.
 *
 * The "prefer primary if nonzero, else fall back" strategy is correct for
 * analytics aggregates — a zero during CF deployment means "not written yet",
 * not "no activity". Once CFs are live, primary fields will be non-zero for
 * any active date.
 *
 * recoveredFromNotYet and notYetCount use sumFieldOrNull: null means the CF
 * has never written the field (pre-deployment rollups). Consumers must treat
 * null as "unknown", not as 0.
 *
 * @param {Array<object>} rollups — array of daily rollup docs from getDailyRollups()
 * @returns {{
 *   gateSubmissions: number,
 *   referralsWritten: number,
 *   enteredOps: number,
 *   recoveredFromNotYet: number|null,
 *   notYetCount: number|null,
 *   triageExceptions: number,
 *   reachedClosedLoop: number,
 *   firstPassAccepted: number,
 *   routineClean: number,
 * }}
 */
export function getFlowQualityTotals(rollups) {
  // Primary flowQuality.* fields (written by CFs after Flow Quality rollout)
  const fqGateSubmissions   = sumField(rollups, 'flowQuality', 'gateSubmissions');
  const fqReferralsWritten  = sumField(rollups, 'flowQuality', 'referralsWritten');
  const fqEnteredOps        = sumField(rollups, 'flowQuality', 'enteredOps');
  const fqRecovered         = sumFieldOrNull(rollups, 'flowQuality', 'recoveredFromNotYet');
  const fqNotYetCount       = sumFieldOrNull(rollups, 'flowQuality', 'notYetCount');
  const fqTriageExceptions  = sumField(rollups, 'flowQuality', 'triageExceptions');
  const fqClosedLoop        = sumField(rollups, 'flowQuality', 'reachedClosedLoop');
  const fqFirstPass         = sumField(rollups, 'flowQuality', 'firstPassAccepted');
  const fqRoutineClean      = sumField(rollups, 'flowQuality', 'routineClean');
  const fqNotYetOverride    = sumFieldOrNull(rollups, 'flowQuality', 'notYetOverrideCount');
  const fqNotYetConverted   = sumFieldOrNull(rollups, 'flowQuality', 'notYetConvertedCount');

  // Legacy fallback fields (pre-CF-rollout data)
  const countsRoutine = sumField(rollups, 'counts', 'routine');
  const countsNotYet  = sumField(rollups, 'counts', 'not_yet');
  const countsUrgent  = sumField(rollups, 'counts', 'urgent');
  const flowCreated   = sumField(rollups, 'flow', 'created');
  const flowClosed    = sumField(rollups, 'flow', 'closedLoop');

  return {
    gateSubmissions:     fqGateSubmissions  || (countsRoutine + countsNotYet + countsUrgent),
    referralsWritten:    fqReferralsWritten  || flowCreated,
    enteredOps:          fqEnteredOps,
    recoveredFromNotYet: fqRecovered,
    notYetCount:         fqNotYetCount,
    triageExceptions:    fqTriageExceptions,
    reachedClosedLoop:   fqClosedLoop        || flowClosed,
    firstPassAccepted:   fqFirstPass         || (countsRoutine + countsUrgent),
    routineClean:        fqRoutineClean      || countsRoutine,
    notYetOverrideCount: fqNotYetOverride,
    notYetConvertedCount: fqNotYetConverted,
  };
}

export default {
  getTodayDateId,
  getDateId,
  buildDateRange,
  getDailyRollup,
  getDailyRollups,
  getOpsSnapshot,
  safeNum,
  sumField,
  sumFieldOrNull,
  getFlowQualityTotals,
};
